import { useNow } from '@/hooks/useNow'
import { relativeTime } from '@/lib/format'

/**
 * Row-level staleness marker, sized to sit beside the flap without crowding it.
 *
 * The timestamp is the whole message, so it is set in mono and everything else
 * is left to the screen-reader text: a held row is still a useful row, and this
 * marker must never out-shout the stage it sits next to.
 */
export function StaleBadge({ updatedAt }: { updatedAt?: string | number | null }) {
  const now = useNow(30_000)
  return (
    <span
      title="Last known data — the home server is unreachable"
      className="inline-flex shrink-0 items-center gap-1 rounded-[3px] border border-warn/30 bg-warn-dim px-1.5 py-1 font-mono text-[10px] leading-none whitespace-nowrap text-warn"
    >
      <span aria-hidden="true" className="size-1 rounded-full bg-warn" />
      <span className="sr-only">Last known data</span>
      {updatedAt ? relativeTime(updatedAt, now) : 'Held'}
    </span>
  )
}

/** Section-level version for lists that were served from the snapshot as a whole. */
export function StaleNotice({ updatedAt }: { updatedAt?: string | number | null }) {
  const now = useNow(30_000)
  return (
    <p className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded border border-warn/20 bg-warn-dim px-3 py-2 text-xs text-muted">
      <span aria-hidden="true" className="size-1.5 shrink-0 rounded-full bg-warn" />
      <span className="signage text-[9px] text-warn">Last known</span>
      <span>The home server is unreachable. This is what Hermes saved.</span>
      {updatedAt ? (
        <span className="font-mono text-faint">Updated {relativeTime(updatedAt, now)}</span>
      ) : null}
    </p>
  )
}
